import React, { useState } from 'react';
import { GitMerge, Loader2, ArrowRight } from 'lucide-react';
import { fiatService } from '../../../../services/api';
import { useIntakeStore } from '../../../../stores/useIntakeStore';
import { toErrorMessage } from '../../../../utils/safeStr';
import { STITCH, STYLES } from '../copy';
import { STITCH_EXAMPLE_JSON } from '../examples';
import type { OffRampToolPanelProps } from '../types';
import { TierBadge } from '../shared/TierBadge';
import { ResultEmptyHint } from '../shared/ResultEmptyHint';

export function StatementStitchPanel({
    className,
    emphasizeExample,
    onComplete,
}: OffRampToolPanelProps) {
    const [eventsText, setEventsText] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    const runStitch = async (text: string) => {
        let events: any[];
        try {
            events = JSON.parse(text || '[]');
            if (!Array.isArray(events)) throw new Error('Expected a JSON array of legs.');
        } catch (e) {
            setError(`Invalid JSON: ${toErrorMessage(e)}`);
            return;
        }
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const caseId = useIntakeStore.getState().caseId;
            const res = await fiatService.stitch(events, caseId);
            setResult(res);
            onComplete?.();
        } catch (e) {
            setError(toErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    const tryExample = () => {
        setEventsText(STITCH_EXAMPLE_JSON);
        void runStitch(STITCH_EXAMPLE_JSON);
    };

    return (
        <div className={`${STYLES.card} ${className ?? ''}`}>
            <ResultEmptyHint>{STITCH.empty}</ResultEmptyHint>
            <textarea
                className={`${STYLES.input} font-mono text-[11px] h-40 resize-y`}
                placeholder='[{"dr_cr":"Dr","account":"A","amount":1000,"ref_number":"412345678901"}, ...]'
                value={eventsText}
                onChange={(e) => setEventsText(e.target.value)}
                aria-label="Statement legs JSON"
            />
            <details className="text-[11px] text-muted-foreground">
                <summary className="cursor-pointer hover:text-foreground">Advanced format</summary>
                <p className="mt-1">{STITCH.advancedNote}</p>
            </details>
            <div className="flex items-center gap-3 flex-wrap">
                <button
                    type="button"
                    className={emphasizeExample ? STYLES.btn : STYLES.btnSecondary}
                    onClick={tryExample}
                    disabled={loading}
                >
                    {loading && emphasizeExample ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <GitMerge className="w-4 h-4" />
                    )}
                    {STITCH.tryExample}
                </button>
                <button
                    type="button"
                    className={emphasizeExample ? STYLES.btnSecondary : STYLES.btn}
                    onClick={() => void runStitch(eventsText)}
                    disabled={loading}
                >
                    {loading && !emphasizeExample ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <GitMerge className="w-4 h-4" />
                    )}
                    {STITCH.run}
                </button>
                {error && <span className="text-xs text-red-500">{error}</span>}
            </div>

            {result && (
                <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">{STITCH.resultHint}</p>
                    <div className="flex gap-2 flex-wrap text-[11px]">
                        <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                            {result.counts?.edges ?? 0} deterministic edge(s)
                        </span>
                        <span className="px-2 py-0.5 rounded bg-secondary text-muted-foreground border border-border">
                            {result.counts?.unstitched ?? 0} unstitched (boundary)
                        </span>
                        <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                            {result.counts?.conflicts ?? 0} conflict(s)
                        </span>
                        <span className="px-2 py-0.5 rounded bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20">
                            {result.counts?.invalid_utr ?? 0} invalid UTR
                        </span>
                    </div>
                    {(result.edges ?? []).map((e: any, i: number) => (
                        <div
                            key={i}
                            className="border border-emerald-500/20 bg-emerald-500/5 rounded-md p-3 text-xs"
                            data-testid="stitch-edge"
                        >
                            <div className="flex items-center gap-2 flex-wrap">
                                <TierBadge determinism="deterministic" />
                                <code className="font-mono">{e.from_ref}</code>
                                <ArrowRight className="w-3 h-3" />
                                <code className="font-mono">{e.to_ref}</code>
                                <span className="font-semibold">
                                    ₹{Number(e.amount).toLocaleString('en-IN')}
                                </span>
                                <span className="text-muted-foreground">{e.rail}</span>
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1 font-mono">
                                UTR {e.utr}
                            </div>
                        </div>
                    ))}
                    {(result.conflicts ?? []).map((c: any, i: number) => (
                        <div
                            key={i}
                            className="border border-amber-500/20 bg-amber-500/5 rounded-md p-3 text-xs text-amber-700 dark:text-amber-300"
                        >
                            Conflict ({c.reason}) on UTR{' '}
                            <code className="font-mono">{c.utr}</code> — not stitched.
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
